import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Truck, Users, Route, Gift, Copy, CreditCard, CalendarClock } from "lucide-react";
import { toast } from "sonner";

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-4 flex items-start gap-3 rounded-md shadow-none" data-testid={testid}>
      <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-display text-xl font-bold">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-muted-foreground">Chargement…</p>;

  const trialDays = data.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(data.trial_ends_at) - new Date()) / 86400000))
    : 0;

  const copyRef = () => {
    navigator.clipboard.writeText(data.referral_code);
    toast.success("Code parrainage copié");
  };

  return (
    <div className="space-y-6" data-testid="dashboard-stats">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Tableau de bord</h1>
          <p className="text-muted-foreground text-sm">Pilotage de votre activité et de votre abonnement.</p>
        </div>
        <Badge
          variant="outline"
          className={data.subscription_status === "active" ? "border-green-500 text-green-600" : "border-accent text-amber-700 dark:text-amber-300 bg-accent/20"}
          data-testid="subscription-badge"
        >
          {data.subscription_status === "active" ? "Abonnement actif" : `Essai gratuit — ${trialDays} j restants`}
        </Badge>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Truck} label="Véhicules actifs" value={data.vehicles} testid="kpi-vehicles" />
        <Kpi icon={Users} label="Conducteurs" value={data.drivers} testid="kpi-drivers" />
        <Kpi icon={Route} label="Courses aujourd'hui" value={data.rides_today} testid="kpi-rides" />
        <Kpi icon={Gift} label="Mois gratuits acquis" value={data.free_months_balance} sub="via parrainage" testid="kpi-free-months" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 rounded-md shadow-none lg:col-span-2">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-semibold">Palier tarifaire en cours</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">{data.tier.label}</p>
          <div className="flex items-end gap-2">
            <span className="font-display text-4xl font-extrabold">{data.tier.total_eur} €</span>
            <span className="text-muted-foreground mb-1.5">/ mois</span>
          </div>
          <div className="mt-3 text-sm text-muted-foreground space-y-0.5">
            <p>Forfait flotte : {data.tier.price_eur} €</p>
            {data.tier.depots_extra_eur > 0 && <p>Dépôts supplémentaires : {data.tier.depots_extra_eur} €</p>}
          </div>
          <Link to="/abonnement">
            <Button className="mt-4" data-testid="dashboard-go-billing">Gérer l'abonnement</Button>
          </Link>
        </Card>

        <Card className="p-5 rounded-md shadow-none">
          <div className="flex items-center gap-2 mb-3">
            <Gift className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-semibold">Parrainage</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-2">Votre code unique</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-secondary rounded-md px-3 py-2 font-mono text-sm" data-testid="referral-code">{data.referral_code}</code>
            <Button size="icon" variant="outline" onClick={copyRef} data-testid="copy-referral"><Copy className="h-4 w-4" /></Button>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            <span className="font-semibold text-foreground">{data.referrals_count}</span> filleul(s) parrainé(s).
            Chaque 1er mois payé d'un filleul = 1 mois offert.
          </p>
        </Card>
      </div>

      <Card className="p-5 rounded-md shadow-none" data-testid="dashboard-invoices">
        <div className="flex items-center gap-2 mb-4">
          <CalendarClock className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-semibold">Historique des factures</h2>
        </div>
        {data.invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune facture pour le moment.</p>
        ) : (
          <div className="divide-y divide-border">
            {data.invoices.map((inv) => (
              <div key={inv.id || inv.created_at} className="flex items-center justify-between py-2.5 text-sm">
                <span>{new Date(inv.created_at).toLocaleDateString("fr-FR")}</span>
                <span className="text-muted-foreground">{inv.metadata?.tier}</span>
                <span className="font-semibold">{inv.amount} €</span>
                <Badge variant="outline" className="border-green-500 text-green-600">Payée</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
